import { useState, useEffect, useMemo, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Save, Loader2, Plus, X, ClipboardPaste, Tag } from 'lucide-react'
import { useYouTubeTagPresets, useSaveYouTubeTagPresets } from '@/api/hooks/useYouTube'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TagPresetEditor({ open, onOpenChange }: Props) {
  const { data: serverPresets } = useYouTubeTagPresets()
  const saveMutation = useSaveYouTubeTagPresets()
  const [activeCategory, setActiveCategory] = useState('')
  const [presets, setPresets] = useState<Record<string, string[]>>({})
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [showBulkPaste, setShowBulkPaste] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [newTag, setNewTag] = useState('')
  const tagInputRef = useRef<HTMLInputElement>(null)

  const categories = useMemo(() => {
    return Object.entries(presets).map(([key, tags]) => ({
      value: key,
      label: key,
      count: tags.length,
    }))
  }, [presets])

  useEffect(() => {
    if (open) {
      const source = serverPresets && Object.keys(serverPresets).length > 0
        ? serverPresets
        : { geral: [] }
      setPresets(JSON.parse(JSON.stringify(source)))
      setActiveCategory(Object.keys(source)[0] || 'geral')
      setShowNewCategory(false)
      setNewCategoryName('')
      setShowBulkPaste(false)
      setBulkText('')
      setNewTag('')
    }
  }, [open, serverPresets])

  const handleSave = () => {
    saveMutation.mutate(presets, {
      onSuccess: () => {
        toast.success('Tag presets salvos!')
        onOpenChange(false)
      },
      onError: () => toast.error('Erro ao salvar'),
    })
  }

  const handleAddCategory = () => {
    const name = newCategoryName.trim().toLowerCase().replace(/\s+/g, '_')
    if (!name) return
    if (presets[name] !== undefined) {
      toast.error('Categoria ja existe')
      return
    }
    setPresets(prev => ({ ...prev, [name]: [] }))
    setActiveCategory(name)
    setShowNewCategory(false)
    setNewCategoryName('')
    toast.success('Categoria criada')
  }

  const handleDeleteCategory = (cat: string) => {
    if (!confirm(`Deletar categoria "${cat}" e todas suas tags?`)) return
    setPresets(prev => {
      const next = { ...prev }
      delete next[cat]
      return next
    })
    if (activeCategory === cat) {
      const remaining = Object.keys(presets).filter(k => k !== cat)
      setActiveCategory(remaining[0] || '')
    }
    toast.success('Categoria removida')
  }

  const handleAddTag = () => {
    const tag = newTag.trim()
    if (!tag || !activeCategory) return
    const current = presets[activeCategory] || []
    if (current.includes(tag)) {
      toast.error('Tag ja existe nesta categoria')
      return
    }
    setPresets(prev => ({
      ...prev,
      [activeCategory]: [...(prev[activeCategory] || []), tag],
    }))
    setNewTag('')
    tagInputRef.current?.focus()
  }

  const handleRemoveTag = (tag: string) => {
    setPresets(prev => ({
      ...prev,
      [activeCategory]: (prev[activeCategory] || []).filter(t => t !== tag),
    }))
  }

  const handleBulkPaste = () => {
    if (!bulkText.trim() || !activeCategory) return
    const newTags = bulkText
      .split(/[,\n]/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
    const current = presets[activeCategory] || []
    const unique = newTags.filter(t => !current.includes(t))
    setPresets(prev => ({
      ...prev,
      [activeCategory]: [...current, ...unique],
    }))
    toast.success(`${unique.length} tags adicionadas!`)
    setShowBulkPaste(false)
    setBulkText('')
  }

  const currentTags = presets[activeCategory] || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Tag className="h-4 w-4" /> Tag Presets do YouTube
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-1">
            Organize suas tags favoritas em categorias. A IA usara elas como referencia ao gerar metadados.
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-1.5">
            {categories.map(cat => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setActiveCategory(cat.value)}
                className={cn(
                  'relative flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] sm:text-xs font-medium transition-colors',
                  activeCategory === cat.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {cat.label}
                <span className={cn(
                  'inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-4 text-[9px] font-bold',
                  activeCategory === cat.value
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-background text-muted-foreground',
                )}>
                  {cat.count}
                </span>
                {activeCategory === cat.value && (
                  <button
                    type="button"
                    className="ml-0.5 inline-flex items-center hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.value) }}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </button>
            ))}
            {showNewCategory ? (
              <div className="flex items-center gap-1">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nome da categoria..."
                  className="h-7 w-36 text-[10px] px-2"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory()
                    if (e.key === 'Escape') { setShowNewCategory(false); setNewCategoryName('') }
                  }}
                />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleAddCategory}>
                  <Plus className="h-3 w-3 text-primary" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setShowNewCategory(false); setNewCategoryName('') }}>
                  <X className="h-3 w-3 text-muted-foreground" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNewCategory(true)}
                className="flex items-center gap-1 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-1.5 text-[11px] sm:text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                <Plus className="h-3 w-3" /> Nova Categoria
              </button>
            )}
          </div>

          {/* Add tag input */}
          {activeCategory && (
            <div className="flex gap-2">
              <Input
                ref={tagInputRef}
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Adicionar tag..."
                className="h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleAddTag() }
                }}
              />
              <Button size="sm" className="h-8 gap-1 text-xs" onClick={handleAddTag}>
                <Plus className="h-3 w-3" /> Adicionar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-xs"
                onClick={() => setShowBulkPaste(!showBulkPaste)}
              >
                <ClipboardPaste className="h-3 w-3" /> Colar
              </Button>
            </div>
          )}

          {/* Bulk paste */}
          {showBulkPaste && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium">Colar tags em massa</p>
                <button
                  type="button"
                  onClick={() => { setShowBulkPaste(false); setBulkText('') }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder="Cole tags separadas por virgula ou uma por linha..."
                className="min-h-[80px] text-xs resize-none"
                autoFocus
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground">
                  {bulkText.split(/[,\n]/).filter(s => s.trim()).length} tags para adicionar
                </p>
                <Button size="sm" className="gap-1.5 text-xs" onClick={handleBulkPaste}>
                  <Plus className="h-3 w-3" /> Adicionar a "{activeCategory}"
                </Button>
              </div>
            </div>
          )}

          {/* Tags display */}
          {activeCategory && (
            <div className="min-h-[120px] max-h-[250px] overflow-y-auto rounded-lg border border-border bg-muted/20 p-3">
              {currentTags.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  Nenhuma tag nesta categoria. Adicione tags acima.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {currentTags.map(tag => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="gap-1 text-[10px] pr-1 hover:bg-destructive/10 transition-colors"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-0.5 rounded-full hover:bg-destructive/20 p-0.5"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground">
              {Object.values(presets).flat().length} tags em {categories.length} categorias
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Salvar
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
